import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Plus, RefreshCw, Loader2, Clock, Hash, FileText, AlertCircle, CheckCircle,
  Circle, Timer, XCircle, Layers, Eye, ChevronRight, ChevronDown, Upload, RotateCcw,
  ChevronsLeft, ChevronsRight, Trash2,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAgentStore } from '@/store/agent-store';
import { createBatch, getBatchStatus, deleteBatchJob, type JobItem, type BatchJobStatus } from '@/api/modules/batch';
import { getBatchJobPlayables, getPlayable, updatePlayable, type PlayableListItem } from '@/api/modules/playables';
import { cn } from '@/utils/cn';
import { CreateBatchDialog } from '@/components/CreateBatchDialog';
import { PublishDialog } from '@/components/publish-dialog';
import { VariantDetailModal, type VariantDetailData } from '@/components/variant-detail-modal';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Select } from '@/components/ui/select';

// ============ Status helpers ============

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string; icon: LucideIcon }> = {
  queued:    { label: '排队中', color: 'text-sky-400',  bg: 'bg-sky-500/15',  icon: Clock },
  waiting:   { label: '等待中', color: 'text-sky-400',  bg: 'bg-sky-500/15',  icon: Clock },
  active:    { label: '运行中', color: 'text-amber-400', bg: 'bg-amber-500/15', icon: Timer },
  running:   { label: '运行中', color: 'text-amber-400', bg: 'bg-amber-500/15', icon: Timer },
  completed: { label: '已完成', color: 'text-neon-green', bg: 'bg-green-500/15', icon: CheckCircle },
  done:      { label: '已完成', color: 'text-neon-green', bg: 'bg-green-500/15', icon: CheckCircle },
  failed:    { label: '失败',   color: 'text-red-400',  bg: 'bg-red-500/15',   icon: XCircle },
};

const VARIANT_STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  draft:     { label: '草稿',   color: 'text-muted-foreground', bg: 'bg-muted/30' },
  generating:{ label: '生成中', color: 'text-sky-400',          bg: 'bg-sky-500/15' },
  pending:   { label: '待发布', color: 'text-amber-400',       bg: 'bg-amber-500/15' },
  published: { label: '已发布', color: 'text-neon-green',       bg: 'bg-green-500/15' },
  failed:    { label: '失败',   color: 'text-red-400',          bg: 'bg-red-500/15' },
};

function formatTime(ts: string | null | undefined): string {
  if (!ts) return '-';
  return new Date(ts).toLocaleString('zh-CN', {
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
}

/** 阶段3：格式化批量任务总耗时（createdAt → finishedAt） */
function formatDuration(start: string | null | undefined, end: string | null | undefined): string {
  if (!start || !end) return '-';
  const ms = new Date(end).getTime() - new Date(start).getTime();
  if (ms < 0 || Number.isNaN(ms)) return '-';
  const totalSec = Math.round(ms / 1000);
  if (totalSec < 60) return `${totalSec}s`;
  const min = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${min}m${sec}s`;
}

function truncateId(id: string): string {
  return id.length > 10 ? `${id.slice(0, 10)}…` : id;
}

function formatGenerationMode(mode: string | null | undefined): string {
  if (!mode) return '-';
  const map: Record<string, string> = {
    template: '内置模板',
    blueprint: '蓝图模式',
    'cocos-source': 'Cocos源码',
    // BUG-20260807-PW-003 前端 GENERATION_MODE_OPTIONS 用 'cocos'，后端归一化为 'cocos-source'，两种值均需映射
    'cocos': 'Cocos源码',
    'cocos-generation': 'Cocos生成',
    video: '视频转试玩',
    'ai-chat': 'AI生成',
    auto: '自动选择',
    'batch-variant': '变体产物',
  };
  return map[mode] ?? mode;
}

// ============ Helpers ============

/** 从任务描述中提取基准试玩名称（格式：基于「xxx」） */
function extractBaseName(description: string | null | undefined): string | null {
  if (!description) return null;
  const m = description.match(/基于「(.+?)」/);
  return m?.[1] ?? null;
}

// ============ Variant Row ============

function VariantRow({
  index,
  baseName,
  variant,
  onPreview,
  onPublish,
  onWithdraw,
  onShowDetail,
}: {
  index: number;
  baseName: string | null;
  variant: PlayableListItem;
  onPreview: (v: PlayableListItem) => void;
  onPublish: (v: PlayableListItem) => void;
  onWithdraw: (v: PlayableListItem) => void;
  onShowDetail: (v: PlayableListItem) => void;
}) {
  const vStatus = VARIANT_STATUS_CONFIG[variant.status] ?? VARIANT_STATUS_CONFIG.draft;
  const isPublished = variant.status === 'published';
  // 阶段3：生成中的占位变体禁止预览/发布/下载，只有完成后才可点击
  const isGenerating = variant.status === 'generating';
  const isFailed = variant.status === 'failed';
  const actionDisabled = isGenerating || isFailed;

  return (
    <div
      className={cn(
        'flex cursor-pointer items-center gap-2 border-b border-border/40 px-4 py-2.5 last:border-b-0 hover:bg-primary/5 transition-colors',
        index % 2 === 1 && 'bg-muted/10',
      )}
      onClick={() => onShowDetail(variant)}
    >
      <span className="shrink-0 w-5 text-center text-[10px] font-mono text-muted-foreground/60">
        {index + 1}
      </span>
      <span className="flex-1 truncate text-xs font-medium text-foreground/90" title={variant.variantLabel || variant.name || '未命名'}>
        {baseName && <span className="text-muted-foreground/70">{baseName} · </span>}
        {variant.variantLabel || variant.name || '未命名'}
        {isGenerating && <Loader2 size={10} className="ml-1 inline animate-spin text-sky-400" />}
      </span>
      <span className={cn('shrink-0 rounded-full px-1.5 py-0.5 text-[9px] font-medium', vStatus.bg, vStatus.color)}>
        {vStatus.label}
      </span>
      <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={() => onPreview(variant)}
          disabled={actionDisabled || !variant.previewUrl}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium text-primary hover:bg-primary/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
        >
          <Eye size={11} />预览
        </button>
        {isPublished ? (
          <button
            onClick={() => onWithdraw(variant)}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium text-red-400 hover:bg-red-500/10 transition-colors"
          >
            <RotateCcw size={11} />撤回
          </button>
        ) : (
          <button
            onClick={() => onPublish(variant)}
            disabled={actionDisabled}
            className="flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-medium text-neon-purple hover:bg-neon-purple/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <Upload size={11} />发布
          </button>
        )}
      </div>
    </div>
  );
}

// ============ Task Item (collapsible) ============

function TaskItem({
  job,
  isExpanded,
  variants,
  variantsLoading,
  onToggle,
  onPreview,
  onPublish,
  onWithdraw,
  onCancel,
  onDelete,
  onShowDetail,
  onPreviewBase,
}: {
  job: JobItem;
  isExpanded: boolean;
  variants: PlayableListItem[];
  variantsLoading: boolean;
  onToggle: () => void;
  onPreview: (v: PlayableListItem) => void;
  onPublish: (v: PlayableListItem) => void;
  onWithdraw: (v: PlayableListItem) => void;
  onCancel: () => void;
  onDelete: () => void;
  onShowDetail: (v: PlayableListItem) => void;
  onPreviewBase: (job: JobItem) => void;
}) {
  const cfg = STATUS_CONFIG[job.status] ?? { label: job.status, color: 'text-muted-foreground', bg: 'bg-muted/30', icon: Circle };
  const StatusIcon = cfg.icon;
  const canCancel = job.status === 'waiting' || job.status === 'queued';
  // 已完成/失败任务可删除（删除任务记录 + 未发布变体，已发布试玩保留）
  const canDelete = (job.status as string) === 'completed' || (job.status as string) === 'done' || (job.status as string) === 'failed';
  const isActive = (job.status as string) === 'active' || (job.status as string) === 'running';
  const isDone = (job.status as string) === 'completed' || (job.status as string) === 'done';
  const isFinished = isDone || (job.status as string) === 'failed';
  const baseName = extractBaseName(job.description);

  // DO-NOT-REVERT: BUG-20260811-PW-020 任务未完结且无真实变体数据时，前端立即按 count 渲染「生成中」占位行，
  // 实现创建即可见（无需等待后端占位落库）；禁止改回"暂无变体数据"空态等待轮询回填。
  const displayVariants: PlayableListItem[] = (variants.length === 0 && !isFinished && job.count)
    ? Array.from({ length: job.count }, (_, i) => ({
        id: `placeholder-${job.jobId}-${i}`,
        name: `变体 ${i + 1}`,
        variantLabel: null,
        description: null,
        previewUrl: null,
        status: 'generating',
        generationMode: 'batch-variant',
        sessionId: '',
        projectId: null,
        projectName: null,
        fileSize: null,
        quality: null,
        createdAt: job.createdAt,
        version: 1,
        sourcePlayableId: null,
        sessionTitle: null,
      } as PlayableListItem))
    : variants;

  return (
    <div className="glass-card overflow-hidden transition-colors">
      {/* Task header — clickable to expand */}
      <div
        className="flex cursor-pointer items-center gap-3 p-3.5 transition-colors hover:border-primary/20"
        onClick={onToggle}
      >
        {/* Expand icon */}
        <div className="shrink-0 text-muted-foreground transition-transform">
          {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </div>

        {/* Task info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <code className="rounded bg-muted/40 px-1.5 py-0.5 text-[11px] text-neon-cyan" title={job.jobId}>
              {truncateId(job.jobId)}
            </code>
            <span className="group relative inline-flex items-center gap-1 min-w-0 text-xs text-foreground/85">
              <FileText size={11} className="shrink-0 text-muted-foreground" />
              <span className="truncate">
                {job.description ?? (job.sessionId ? `会话 ${job.sessionId.slice(0, 8)}…` : '批量任务')}
              </span>
              {job.description && (
                <span className="pointer-events-none invisible absolute left-0 top-full z-50 mt-1 w-max max-w-lg whitespace-pre-wrap break-all rounded border border-yellow-300 bg-yellow-50 px-2.5 py-1.5 text-[11px] text-yellow-900 shadow-md group-hover:visible">
                  {job.description}
                </span>
              )}
            </span>
          </div>
          <div className="mt-1 flex items-center gap-2 text-[10px] text-muted-foreground">
            <span className={cn('inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 font-medium', cfg.bg, cfg.color)}>
              <StatusIcon size={9} />{cfg.label}
            </span>
            {job.generationMode && (
              <span className="rounded bg-muted/40 px-1.5 py-0.5">{formatGenerationMode(job.generationMode)}</span>
            )}
            <span className="flex items-center gap-0.5"><Hash size={9} />{job.count ?? '-'}</span>
            {isActive && (
              <span className="inline-flex items-center gap-0.5 text-amber-400">
                <Loader2 size={9} className="animate-spin" />
                {job.count ? `${job.completed}/${job.count}` : '进行中'}
              </span>
            )}
            {isDone && <span className="text-neon-green">{job.count ? `${job.completed}/${job.count}` : '完成'}</span>}
            <span className="flex items-center gap-0.5"><Clock size={9} />{formatTime(job.createdAt)}</span>
            {/* 阶段3：任务完成后展示总耗时 */}
            {isDone && job.finishedAt && (
              <span className="flex items-center gap-0.5 text-neon-green" title="批量任务总耗时">
                <Timer size={9} />耗时 {formatDuration(job.createdAt, job.finishedAt)}
              </span>
            )}
          </div>
        </div>

        {/* Action buttons (stop propagation so click doesn't toggle) */}
        <div className="flex shrink-0 items-center gap-1" onClick={(e) => e.stopPropagation()}>
          {job.sourcePlayableId && (
            <button
              onClick={() => onPreviewBase(job)}
              className="icon-btn flex items-center gap-1 rounded p-1.5 text-[10px] text-neon-cyan hover:bg-neon-cyan/10 transition-colors"
              title="预览基准变体"
            >
              <Eye size={12} />
            </button>
          )}
          {job.status === 'failed' && job.failedReason && (
            <span className="mr-1 truncate text-[10px] text-red-400/80 max-w-[120px]" title={job.failedReason}>
              <AlertCircle size={10} className="inline" /> {job.failedReason.slice(0, 20)}
            </span>
          )}
          {canCancel && (
            <button onClick={onCancel}
              className="icon-btn danger rounded p-1.5" title="取消任务">
              <XCircle size={12} />
            </button>
          )}
          {canDelete && (
            <button onClick={onDelete}
              className="icon-btn danger rounded p-1.5" title="删除任务（未发布变体一并删除，已发布试玩保留）">
              <Trash2 size={12} />
            </button>
          )}
        </div>

        {/* Variant count badge */}
        {isExpanded && displayVariants.length > 0 && (
          <span className="shrink-0 rounded-full bg-neon-cyan/10 px-2 py-0.5 text-[10px] font-medium text-neon-cyan">
            {displayVariants.length} 个变体
          </span>
        )}
      </div>

      {/* Expanded variant list */}
      {isExpanded && (
        <div className="border-t border-border/20 bg-muted/5">
          {variantsLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 size={18} className="animate-spin text-neon-cyan" />
              <span className="ml-2 text-xs text-muted-foreground">加载变体中…</span>
            </div>
          ) : displayVariants.length === 0 ? (
            <div className="py-6 text-center text-xs text-muted-foreground">
              暂无变体数据
            </div>
          ) : (
            <div>
              {displayVariants.map((v, i) => (
                <VariantRow
                  key={v.id}
                  index={i}
                  baseName={baseName}
                  variant={v}
                  onPreview={onPreview}
                  onPublish={onPublish}
                  onWithdraw={onWithdraw}
                  onShowDetail={onShowDetail}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ============ BatchManager Page ============

export function BatchManager(): JSX.Element {
  const jobs = useAgentStore((s) => s.jobs);
  const jobsLoading = useAgentStore((s) => s.jobsLoading);
  const fetchJobs = useAgentStore((s) => s.fetchJobs);
  const cancelJob = useAgentStore((s) => s.cancelJob);
  const [searchParams, setSearchParams] = useSearchParams();

  // Read URL params for pre-selected baseline playable
  const urlSourcePlayableId = searchParams.get('sourcePlayableId') ?? undefined;

  const [showCreate, setShowCreate] = useState(false);
  const [filterMode, setFilterMode] = useState('');
  // 分页状态（需求：创意工厂任务列表分页展示）
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 10;

  // Collapsible state
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set());
  // Variants cache per job
  const [variantsMap, setVariantsMap] = useState<Record<string, PlayableListItem[]>>({});
  const [loadingVariants, setLoadingVariants] = useState<Set<string>>(new Set());

  // Publish dialog state
  const [publishVariant, setPublishVariant] = useState<{ id: string; defaultName: string; status: string } | null>(null);
  const [publishLoading, setPublishLoading] = useState(false);

  // Variant detail modal state
  const [detailVariant, setDetailVariant] = useState<VariantDetailData | null>(null);

  // Auto-open create dialog if URL has sourcePlayableId
  useEffect(() => {
    if (urlSourcePlayableId && !showCreate) {
      setShowCreate(true);
    }
  }, [urlSourcePlayableId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchJobs(); }, [fetchJobs]);

  // DO-NOT-REVERT: BUG-20260811-PW-020 仅首次加载展示全屏 loading，
  // 后续轮询 fetchJobs 不再触发整列表闪烁（原 jobsLoading 每次轮询置 true 导致反复刷新观感）。
  const [initialLoading, setInitialLoading] = useState(true);
  useEffect(() => {
    if (!jobsLoading && initialLoading) setInitialLoading(false);
  }, [jobsLoading, initialLoading]);

  // 获取变体列表（提取为独立函数，供展开和轮询复用）
  const fetchVariants = useCallback(async (jobId: string) => {
    setLoadingVariants((prev) => new Set(prev).add(jobId));
    try {
      const res = await getBatchJobPlayables(jobId);
      if (res.success && res.data) {
        setVariantsMap((prev) => ({ ...prev, [jobId]: res.data }));
        return;
      }
      // API returned success=false on HTTP 200 → fallback
      throw new Error('getBatchJobPlayables returned success=false');
    } catch {
      // Fallback: try getBatchStatus for variant data
      try {
        const batchRes = await getBatchStatus(jobId);
        if (batchRes.success && batchRes.data) {
          const src = batchRes.data?.variants ?? batchRes.data?.returnValue?.variants ?? [];
          const fallbackVariants: PlayableListItem[] = src.map((v: any, i: number) => ({
            id: v.id ?? `fallback-${jobId}-${i}`,
            name: v.name ?? v.variantLabel ?? `变体 ${i + 1}`,
            variantLabel: v.variantLabel ?? v.name ?? null,
            description: v.description ?? null,
            previewUrl: v.previewUrl ?? null,
            status: v.status ?? 'draft',
            generationMode: v.generationMode ?? 'batch-variant',
            sessionId: v.sessionId ?? batchRes.data?.sessionId ?? '',
            projectId: v.projectId ?? null,
            projectName: v.projectName ?? null,
            fileSize: v.fileSize ?? v.size ?? null,
            quality: v.quality ?? null,
            createdAt: v.createdAt ?? new Date().toISOString(),
            version: v.version ?? 1,
            sourcePlayableId: null,
            sessionTitle: null,
          }));
          setVariantsMap((prev) => ({ ...prev, [jobId]: fallbackVariants }));
        }
      } catch { /* ignore */ }
    } finally {
      setLoadingVariants((prev) => { const next = new Set(prev); next.delete(jobId); return next; });
    }
  }, []);

  // DO-NOT-REVERT: BUG-20260811-PW-020 运行中仅轮询任务状态，变体列表由前端占位行展示，
  // 禁止恢复 PW-019 方案在 active 阶段周期 fetchVariants（反复刷新且首刷早于占位落库显示空态）。
  // 依赖仅 hasActive，保证 interval 稳定不随 jobs 更新重建。
  const hasActive = jobs.some((j) => j.status === 'active' || j.status === 'waiting' || j.status === 'queued');
  useEffect(() => {
    if (!hasActive) return;
    const timer = setInterval(() => { fetchJobs(); }, 5000);
    return () => clearInterval(timer);
  }, [hasActive, fetchJobs]);

  // 任务完结（completed/failed）后统一拉取一次变体列表（每个任务只拉一次）
  const finalFetchedRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    for (const job of jobs) {
      const isFinished = job.status === 'completed' || (job.status as string) === 'done' || job.status === 'failed';
      if (!isFinished || finalFetchedRef.current.has(job.jobId)) continue;
      if (!expandedJobs.has(job.jobId)) continue;
      finalFetchedRef.current.add(job.jobId);
      fetchVariants(job.jobId);
    }
  }, [jobs, expandedJobs, fetchVariants]);

  // Toggle expand/collapse and fetch variants on expand
  const handleToggleExpand = useCallback((jobId: string) => {
    const isExpanding = !expandedJobs.has(jobId);
    setExpandedJobs((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) {
        next.delete(jobId);
      } else {
        next.add(jobId);
      }
      return next;
    });

    // 已完结任务展开时拉取真实变体；运行中任务由前端占位行展示，不发请求（完成后统一刷新）
    if (isExpanding) {
      const job = jobs.find((j) => j.jobId === jobId);
      const isFinished = job && ((job.status as string) === 'completed' || (job.status as string) === 'done' || (job.status as string) === 'failed');
      if (isFinished) {
        fetchVariants(jobId);
      }
    }
  }, [expandedJobs, fetchVariants, jobs]);

  const handleCreate = useCallback(async (desc: string, count: number, sourcePlayableId?: string, projectId?: string, dimensions?: Array<{ name: string; values: string[] }>) => {
    try {
      const res = await createBatch({ description: desc, count, sourcePlayableId, projectId, dimensions });
      toast.success(`批量任务已创建 (${res.data.jobId.slice(0, 8)}…)`);
      setShowCreate(false);
      setSearchParams({});
      // DO-NOT-REVERT: BUG-20260811-PW-019 创建后自动展开新任务，
      // 配合轮询在占位记录落库后立即渲染「生成中」变体行（创建即可见），禁止移除。
      if (res.data?.jobId) {
        setExpandedJobs((prev) => new Set(prev).add(res.data.jobId));
      }
      await fetchJobs();
    } catch {
      toast.error('创建失败，请确认后端服务已启动');
    }
  }, [fetchJobs, setSearchParams]);

  const handleCancel = useCallback(async (jobId: string) => {
    try {
      await cancelJob(jobId);
      toast.success('任务已取消');
    } catch {
      toast.error('取消失败');
    }
  }, [cancelJob]);

  const { confirm, dialog } = useConfirm();

  /** 删除已完成/失败的批量任务：删除任务记录 + 未发布变体，已发布试玩保留在试玩列表 */
  const handleDelete = useCallback((job: JobItem) => {
    const desc = job.description ?? job.jobId;
    confirm('删除任务', `确定删除任务「${desc}」吗？将删除任务记录及其中未发布变体，已发布的试玩会保留在试玩列表。`, async () => {
      try {
        const res = await deleteBatchJob(job.jobId);
        if (!res.success) {
          toast.error((res as { message?: string }).message ?? '删除失败，请重试');
          return;
        }
        toast.success(`任务已删除${res.data.removedVariants > 0 ? `，同时清理 ${res.data.removedVariants} 个未发布变体` : ''}`);
        // 清理本地缓存，避免 jobId 复用/残留数据
        finalFetchedRef.current.delete(job.jobId);
        setVariantsMap((prev) => { const next = { ...prev }; delete next[job.jobId]; return next; });
        setExpandedJobs((prev) => { const next = new Set(prev); next.delete(job.jobId); return next; });
        await fetchJobs();
      } catch {
        toast.error('删除失败，请确认后端服务已启动');
      }
    });
  }, [deleteBatchJob, fetchJobs, confirm]);

  const handlePreview = useCallback((v: PlayableListItem) => {
    if (v.previewUrl) {
      window.open(v.previewUrl, '_blank', 'noopener,noreferrer');
    }
  }, []);

  const handlePreviewBase = useCallback(async (job: JobItem) => {
    if (!job.sourcePlayableId) return;
    try {
      const res = await getPlayable(job.sourcePlayableId);
      if (res.success && res.data?.previewUrl) {
        window.open(res.data.previewUrl, '_blank', 'noopener,noreferrer');
      } else {
        toast.error('基准变体暂无预览链接');
      }
    } catch {
      toast.error('获取基准变体信息失败');
    }
  }, []);

  const handlePublish = useCallback((v: PlayableListItem) => {
    setPublishVariant({
      id: v.id,
      defaultName: v.variantLabel || v.name || '未命名试玩',
      status: v.status,
    });
  }, []);

  const handleWithdraw = useCallback((v: PlayableListItem) => {
    confirm('撤回试玩', `确定撤回「${v.variantLabel || v.name || '未命名试玩'}」吗？撤回后将从已发布列表中移除，可重新发布。`, async () => {
      try {
        // 真实撤回：PATCH 将试玩 status 置为 pending（已发布 → 待发布），试玩列表自动移除
        await updatePlayable(v.id, { status: 'pending' });
        toast.success('已撤回，变体已转为待发布');
        // 同步本地缓存状态（与发布逻辑一致）
        setVariantsMap((prev) => {
          const updated = { ...prev };
          for (const [jobId, variants] of Object.entries(updated)) {
            updated[jobId] = variants.map((x) =>
              x.id === v.id ? { ...x, status: 'pending' } : x,
            );
          }
          return updated;
        });
      } catch {
        toast.error('撤回失败，请重试');
      }
    });
  }, [confirm]);

  const handlePublishConfirm = useCallback(async (name: string) => {
    if (!publishVariant) return;
    setPublishLoading(true);
    try {
      // 真实发布：PATCH 将试玩 status 置为 published，持久化后才在试玩列表/资产中展示
      await updatePlayable(publishVariant.id, { status: 'published', variantLabel: name });
      toast.success(`发布成功！试玩名称：${name}`);
      // Update variant status in local cache
      setVariantsMap((prev) => {
        const updated = { ...prev };
        for (const [jobId, variants] of Object.entries(updated)) {
          updated[jobId] = variants.map((v) =>
            v.id === publishVariant.id ? { ...v, status: 'published', variantLabel: name } : v,
          );
        }
        return updated;
      });
      setPublishVariant(null);
    } catch {
      toast.error('发布失败，请确认后端服务已启动');
    } finally {
      setPublishLoading(false);
    }
  }, [publishVariant]);

  const filteredJobs = useMemo(() => {
    return jobs.filter((j) => {
      if (j.generationMethod !== 'variant') return false;
      if (filterMode && j.generationMode !== filterMode) return false;
      return true;
    });
  }, [jobs, filterMode]);

  // 分页：仅过滤条件变化时回到第一页（不依赖 jobs，避免轮询刷新时跳回第一页）
  useEffect(() => {
    setPage(1);
  }, [filterMode]);

  const totalPages = Math.max(1, Math.ceil(filteredJobs.length / PAGE_SIZE));
  const safePage = Math.min(page, totalPages);
  const pageJobs = useMemo(
    () => filteredJobs.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filteredJobs, safePage],
  );

  return (
    <div className="flex flex-1 flex-col">
      {/* Header */}
      <div className="page-header">
        <div>
          <h2 className="page-title" style={{ animation: 'none' }}>
            <Layers size={18} className="shrink-0 text-neon-cyan" />
            创意工厂
          </h2>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => fetchJobs()} disabled={jobsLoading}
            className="icon-btn flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm">
            <RefreshCw size={14} className={jobsLoading ? 'animate-spin' : ''} />刷新
          </button>
          <button onClick={() => setShowCreate(true)}
            className="glow-btn flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium">
            <Plus size={15} />新建批量任务
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <Select
          options={[
            { value: '', label: '全部构建模式' },
            { value: 'template', label: '内置模板' },
            { value: 'blueprint', label: '蓝图模式' },
            { value: 'cocos-source', label: 'Cocos源码' },
            { value: 'video', label: '视频转试玩' },
          ]}
          value={filterMode}
          onChange={setFilterMode}
          className="input-neon py-2 px-3 text-sm"
        />
        <span className="text-xs text-muted-foreground">共 {filteredJobs.length} 个任务</span>      </div>

      {/* Job list */}
      {initialLoading ? (
        <div className="flex flex-1 flex-col items-center justify-start gap-3 pt-28">
          <Loader2 size={32} className="animate-spin text-neon-cyan" />
          <span className="text-xs text-muted-foreground">加载中...</span>
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-20">
          <Layers size={40} className="text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">暂无批量任务</p>
          <p className="text-xs text-muted-foreground/70">点击"新建批量任务"一键批量生成试玩变体</p>
          <button onClick={() => setShowCreate(true)}
            className="glow-btn mt-2 flex items-center gap-1.5 rounded-xl px-4 py-2 text-sm font-medium">
            <Plus size={15} />新建批量任务
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-3 stagger-children">
          {pageJobs.map((job) => (
            <TaskItem
              key={job.jobId}
              job={job}
              isExpanded={expandedJobs.has(job.jobId)}
              variants={variantsMap[job.jobId] ?? []}
              variantsLoading={loadingVariants.has(job.jobId)}
              onToggle={() => handleToggleExpand(job.jobId)}
              onPreview={handlePreview}
              onPublish={handlePublish}
              onCancel={() => handleCancel(job.jobId)}
              onDelete={() => handleDelete(job)}
              onWithdraw={handleWithdraw}
              onShowDetail={(v) => setDetailVariant(v as VariantDetailData)}
              onPreviewBase={handlePreviewBase}
            />
          ))}

          {/* Stats footer */}
          <div className="flex items-center justify-between pt-1 text-[11px] text-muted-foreground">
            <span>共 {filteredJobs.length} 个任务</span>
            <span>
              {filteredJobs.filter((j) => (j.status as string) === 'active' || (j.status as string) === 'waiting' || (j.status as string) === 'queued').length} 排队 / {filteredJobs.filter((j) => (j.status as string) === 'completed' || (j.status as string) === 'done').length} 完成 / {filteredJobs.filter((j) => (j.status as string) === 'failed').length} 失败
            </span>
          </div>

          {/* 分页控件 */}
          {filteredJobs.length > PAGE_SIZE && (
            <div className="flex items-center justify-center gap-2 pt-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={safePage <= 1}
                className="icon-btn flex items-center gap-1 rounded-lg border border-border/50 px-3 py-1.5 text-xs disabled:opacity-40"
              >
                <ChevronsLeft size={12} />上一页
              </button>
              <span className="text-xs text-muted-foreground">{safePage} / {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={safePage >= totalPages}
                className="icon-btn flex items-center gap-1 rounded-lg border border-border/50 px-3 py-1.5 text-xs disabled:opacity-40"
              >
                下一页<ChevronsRight size={12} />
              </button>
            </div>
          )}
        </div>
      )}

      <CreateBatchDialog open={showCreate} onClose={() => { setShowCreate(false); setSearchParams({}); }} onCreate={handleCreate} initialPlayableId={urlSourcePlayableId} />

      <PublishDialog
        open={!!publishVariant}
        defaultName={publishVariant?.defaultName ?? ''}
        loading={publishLoading}
        onConfirm={handlePublishConfirm}
        onCancel={() => { if (!publishLoading) setPublishVariant(null); }}
      />

      <VariantDetailModal
        open={!!detailVariant}
        variant={detailVariant}
        onClose={() => setDetailVariant(null)}
      />
      {dialog}
    </div>
  );
}
